import { type Connection, PublicKey } from '@solana/web3.js';
import type BN from 'bn.js';
import type { OutcomePreparation } from '../builders/market-preparation.js';
import { MORFI_OPINION_PROGRAM_ID } from '../constants/programs.js';
import { getProgram } from '../idl/loader.js';
import { deriveMarketPda } from '../pda/derivations.js';
import { isMetadataName, isMetadataSymbol } from '../utils/human-text.js';
import { IncompatiblePreparationAccountError } from './preparation-errors.js';

export interface StagedOutcomeMetadata {
  market: PublicKey;
  creator: PublicKey;
  marketNonce: BN;
  initialOutcomes: number;
  outcomes: OutcomePreparation[];
}

export interface StagedOutcomeMetadataIdentity {
  market: PublicKey;
  creator: PublicKey;
  marketNonce: BN;
  initialOutcomes?: number;
}

export async function fetchStagedOutcomeMetadata(
  connection: Connection,
  identity: StagedOutcomeMetadataIdentity,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
  minContextSlot: number = 0,
  commitment: 'confirmed' | 'finalized' = 'confirmed',
): Promise<StagedOutcomeMetadata | null> {
  if (
    !deriveMarketPda(identity.creator, identity.marketNonce, programId)[0].equals(identity.market)
  )
    throw new IncompatiblePreparationAccountError('Staged metadata Market PDA is invalid');
  const program = await getProgram(connection, programId);
  const address = PublicKey.findProgramAddressSync(
    [Buffer.from('outcome_metadata'), identity.market.toBuffer()],
    programId,
  )[0];
  const snapshot = await connection.getAccountInfoAndContext(address, {
    commitment,
    minContextSlot,
  });
  if (snapshot.context.slot < minContextSlot)
    throw new Error('Staged metadata RPC context is behind');
  const info = snapshot.value;
  if (info === null) return null;
  if (!info.owner.equals(programId))
    throw new IncompatiblePreparationAccountError('Staged metadata has an invalid owner');
  let metadata: StagedOutcomeMetadata;
  try {
    metadata = program.coder.accounts.decode<StagedOutcomeMetadata>('outcomeMetadata', info.data);
  } catch {
    throw new IncompatiblePreparationAccountError('Staged metadata has an incompatible layout');
  }
  if (
    !metadata.market.equals(identity.market) ||
    !metadata.creator.equals(identity.creator) ||
    !metadata.marketNonce.eq(identity.marketNonce) ||
    metadata.initialOutcomes < 2 ||
    metadata.initialOutcomes > 10 ||
    (identity.initialOutcomes !== undefined &&
      metadata.initialOutcomes !== identity.initialOutcomes)
  )
    throw new IncompatiblePreparationAccountError('Staged metadata identity differs from request');
  if (metadata.outcomes.length < 1 || metadata.outcomes.length > 10)
    throw new IncompatiblePreparationAccountError('Staged metadata prefix is invalid');
  validateStagedTuples(metadata.outcomes);
  return metadata;
}

export function validateStagedTuples(outcomes: OutcomePreparation[]): void {
  for (const [index, outcome] of outcomes.entries()) {
    if (
      outcome.outcomeIndex !== index ||
      !isMetadataName(outcome.name) ||
      !isMetadataSymbol(outcome.symbol) ||
      Buffer.byteLength(outcome.uri, 'utf8') === 0 ||
      Buffer.byteLength(outcome.uri, 'utf8') > 200 ||
      !/^ipfs:\/\/b[a-z2-7]+$/.test(outcome.uri)
    )
      throw new IncompatiblePreparationAccountError(
        'Staged metadata contains an invalid outcome tuple',
      );
  }
}
